import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVoiceCommands } from '../context/VoiceCommandContext';
import { Settings as SettingsIcon, Bell, Palette, Layout, Monitor, Moon, Sun, CheckCircle, AlertCircle, Mail, Server, Lock, Calendar, RefreshCw, Copy, DollarSign, Globe, Clock, Mic, Sparkles } from 'lucide-react';
import { refreshTimezoneSettings } from '../utils/dateUtils';
import StaffTrustAccounts from '../components/settings/StaffTrustAccounts';
import MeshSettings from '../components/settings/MeshSettings';
import SupportAccessPanel from '../components/settings/SupportAccessPanel';
import PortalBannerAdmin from '../components/settings/PortalBannerAdmin';
import SpaceBackground from '../components/layout/SpaceBackground';

const Settings = () => {
  const { user } = useAuth();
  const { setEnabled: setVoiceEnabled, recognitionSupported } = useVoiceCommands();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [settings, setSettings] = useState({
    // Appearance
    theme: 'light',
    sidebarCollapsed: false,
    compactMode: false,

    // Notifications
    notifyNewCase: true,
    notifyNewMessage: true,
    notifyCalendarReminder: true,
    notifyTaskDue: true,
    soundEnabled: true,

    // Default views
    defaultCasesView: 'list',
    defaultContactsView: 'list',
    casesPerPage: 25,

    // Work preferences
    autoSaveEnabled: true,
    autoSaveInterval: 30,

    // Astraea voice settings
    astraeaMicAlwaysOn: false,
    astraeaMicPreferenceAsked: false,
  });

  const [mailMode, setMailMode] = useState('client');
  const [mailSettings, setMailSettings] = useState({
    imap_server: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    imap_use_ssl: true,
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_ssl: true
  });
  const [showPassword, setShowPassword] = useState(false);

  // Firm Billing Settings State
  const [billingSettings, setBillingSettings] = useState({
    attorney_rate: 350.00,
    partner_rate: 450.00,
    associate_rate: 275.00,
    paralegal_rate: 150.00,
    admin_rate: 75.00,
    default_billing_method: 'hourly',
    default_contingency_rate: 33.33,
    minimum_increment: 0.1,
    rounding_method: 'up',
    default_tax_rate: 0.0,
    payment_terms_days: 30,
    late_fee_percentage: 0.0
  });
  const [billingLoading, setBillingLoading] = useState(false);

  // CalDAV Device Passwords State
  const [devicePasswords, setDevicePasswords] = useState([]);
  const [newDeviceLabel, setNewDeviceLabel] = useState('');
  const [newDevicePassword, setNewDevicePassword] = useState(null); // shown once after mint
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [devicePwCopied, setDevicePwCopied] = useState(false);

  // Calendar Feed State
  const [feedStatus, setFeedStatus] = useState(null);
  const [feedUrls, setFeedUrls] = useState(null); // raw URLs, shown once after mint
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedCopied, setFeedCopied] = useState('');

  // Timezone Settings State
  const [timezoneSettings, setTimezoneSettings] = useState({
    timezone: 'America/New_York',
    timezone_override_enabled: true
  });
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [timezoneLoading, setTimezoneLoading] = useState(false);

  useEffect(() => {
    loadSettings();
    loadMailMode();
    loadMailSettings();
    loadBillingSettings();
    loadTimezoneSettings();
    loadFeedStatus();
    loadDevicePasswords();
  }, []);

  // CalDAV device password functions
  const loadDevicePasswords = async () => {
    try {
      const response = await fetch('/api/calendar/device-passwords', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDevicePasswords(data.device_passwords || []);
      }
    } catch (err) {
      console.error('Failed to load device passwords:', err);
    }
  };

  const mintDevicePassword = async () => {
    const label = newDeviceLabel.trim();
    if (!label) {
      setError('Enter a name for this device (e.g. "iPhone")');
      setTimeout(() => setError(''), 4000);
      return;
    }
    try {
      setDeviceLoading(true);
      const response = await fetch('/api/calendar/device-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ label })
      });
      const data = await response.json();
      if (response.ok) {
        setNewDevicePassword({ label: data.label, password: data.password });
        setNewDeviceLabel('');
        setSuccess('Device password created');
        setTimeout(() => setSuccess(''), 3000);
        await loadDevicePasswords();
      } else {
        throw new Error(data.detail || 'Failed to create device password');
      }
    } catch (err) {
      setError('Failed to create device password: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeviceLoading(false);
    }
  };

  const revokeDevicePassword = async (id) => {
    try {
      setDeviceLoading(true);
      const response = await fetch(`/api/calendar/device-password/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setSuccess('Device password revoked');
        setTimeout(() => setSuccess(''), 3000);
        await loadDevicePasswords();
      } else {
        throw new Error('Failed to revoke device password');
      }
    } catch (err) {
      setError('Failed to revoke device password: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeviceLoading(false);
    }
  };

  const copyDevicePassword = async (pw) => {
    try {
      await navigator.clipboard.writeText(pw);
      setDevicePwCopied(true);
      setTimeout(() => setDevicePwCopied(false), 2000);
    } catch (err) {
      setError('Copy failed — select and copy the password manually');
      setTimeout(() => setError(''), 4000);
    }
  };

  // Calendar Feed functions
  const loadFeedStatus = async () => {
    try {
      const response = await fetch('/api/calendar/feed-token', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setFeedStatus(await response.json());
      }
    } catch (err) {
      console.error('Failed to load calendar feed status:', err);
    }
  };

  const mintFeedToken = async () => {
    try {
      setFeedLoading(true);
      const response = await fetch('/api/calendar/feed-token', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (response.ok) {
        setFeedUrls(data);
        setSuccess('Calendar feed link generated');
        setTimeout(() => setSuccess(''), 3000);
        await loadFeedStatus();
      } else {
        throw new Error(data.detail || 'Failed to generate feed link');
      }
    } catch (err) {
      setError('Failed to generate calendar feed link: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setFeedLoading(false);
    }
  };

  const revokeFeedToken = async () => {
    try {
      setFeedLoading(true);
      const response = await fetch('/api/calendar/feed-token', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setFeedUrls(null);
        setSuccess('Calendar feed link revoked');
        setTimeout(() => setSuccess(''), 3000);
        await loadFeedStatus();
      } else {
        throw new Error('Failed to revoke feed link');
      }
    } catch (err) {
      setError('Failed to revoke calendar feed link: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setFeedLoading(false);
    }
  };

  const copyFeedUrl = async (kind, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setFeedCopied(kind);
      setTimeout(() => setFeedCopied(''), 2000);
    } catch (err) {
      setError('Copy failed — select and copy the URL manually');
      setTimeout(() => setError(''), 4000);
    }
  };

  const loadSettings = () => {
    // Load from localStorage for now
    const saved = localStorage.getItem('virgo_settings');
    if (saved) {
      try {
        setSettings(prev => ({ ...prev, ...JSON.parse(saved) }));
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }
  };


  const loadMailMode = async () => {
    try {
      const response = await fetch('/api/system/settings/mail_mode', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMailMode(data.value);
      }
    } catch (err) {
      console.error('Failed to load mail mode:', err);
    }
  };

  const loadMailSettings = async () => {
    try {
      const response = await fetch('/api/user/mail-settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMailSettings(data);
      }
    } catch (err) {
      console.error('Failed to load mail settings:', err);
    }
  };

  const saveMailSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/user/mail-settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mailSettings)
      });
      
      if (response.ok) {
        setSuccess('Email settings saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      setError('Failed to save email settings: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleMailSettingChange = (field, value) => {
    setMailSettings(prev => ({ ...prev, [field]: value }));
  };

  // Billing Settings Functions
  const loadBillingSettings = async () => {
    try {
      const response = await fetch('/api/cases/settings/billing', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBillingSettings(data);
      }
    } catch (err) {
      console.error('Failed to load billing settings:', err);
    }
  };

  const saveBillingSettings = async () => {
    try {
      setBillingLoading(true);
      const response = await fetch('/api/cases/settings/billing', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(billingSettings)
      });

      if (response.ok) {
        setSuccess('Billing settings saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save settings');
      }
    } catch (err) {
      setError('Failed to save billing settings: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleBillingSettingChange = (field, value) => {
    setBillingSettings(prev => ({ ...prev, [field]: value }));
  };

  // Timezone Settings Functions
  const loadTimezoneSettings = async () => {
    try {
      // Load current settings
      const settingsResponse = await fetch('/api/timezone', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (settingsResponse.ok) {
        const data = await settingsResponse.json();
        setTimezoneSettings({
          timezone: data.timezone || 'America/New_York',
          timezone_override_enabled: data.timezone_override_enabled !== false
        });
      }

      // Load timezone options
      const optionsResponse = await fetch('/api/timezone/options', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (optionsResponse.ok) {
        const options = await optionsResponse.json();
        setTimezoneOptions(options);
      }
    } catch (err) {
      console.error('Failed to load timezone settings:', err);
    }
  };

  const saveTimezoneSettings = async () => {
    try {
      setTimezoneLoading(true);
      const response = await fetch('/api/timezone', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(timezoneSettings)
      });

      if (response.ok) {
        setSuccess('Timezone settings saved successfully');
        // Refresh the cached timezone in dateUtils
        await refreshTimezoneSettings();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save settings');
      }
    } catch (err) {
      setError('Failed to save timezone settings: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setTimezoneLoading(false);
    }
  };

  const saveSettings = () => {
    setLoading(true);
    setError('');
    
    try {
      localStorage.setItem('virgo_settings', JSON.stringify(settings));
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleAstraeaMicChange = (alwaysOn) => {
    // Update local state
    setSettings(prev => ({
      ...prev,
      astraeaMicAlwaysOn: alwaysOn,
      astraeaMicPreferenceAsked: true
    }));

    // Save to localStorage immediately
    const saved = JSON.parse(localStorage.getItem('virgo_settings') || '{}');
    saved.astraeaMicAlwaysOn = alwaysOn;
    saved.astraeaMicPreferenceAsked = true;
    localStorage.setItem('virgo_settings', JSON.stringify(saved));

    // Update voice command context
    setVoiceEnabled(alwaysOn);

    setSuccess(`Astraea voice ${alwaysOn ? 'enabled' : 'disabled'} successfully`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-virgo-teal-600' : 'bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-virgo-navy-700 transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className="max-w-4xl mx-auto relative">
      <SpaceBackground />
      <div className="relative z-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-virgo-teal-100 flex items-center">
          <SettingsIcon className="mr-3" size={32} />
          Settings
        </h1>
        <button
          onClick={saveSettings}
          disabled={loading}
          className="bg-virgo-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition disabled:bg-gray-400"
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Firm-level pages that live outside this personal-prefs form; the
          formatting/ePortal route was previously reachable only by URL */}
      <Link to="/settings/formatting"
        className="mb-4 flex items-center justify-between p-3 rounded-lg border border-virgo-teal-700/40 bg-virgo-navy-800/40 hover:bg-virgo-teal-700/20 transition">
        <span className="flex items-center text-virgo-teal-100 text-sm font-medium">
          <Server className="mr-2" size={16} />
          Firm Formatting &amp; ePortal Credentials
        </span>
        <span className="text-xs text-virgo-navy-200">
          court filings, document formatting, e-filing portal login →
        </span>
      </Link>

      <PortalBannerAdmin />

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
          <AlertCircle size={20} className="mr-2" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-center">
          <CheckCircle size={20} className="mr-2" />
          <span>{success}</span>
        </div>
      )}

      {/* Appearance */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
          <Palette className="mr-2 text-virgo-teal-300" size={20} />
          Appearance
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Theme</p>
              <p className="text-sm text-virgo-teal-400">Choose your preferred color theme</p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handleChange('theme', 'light')}
                className={`flex items-center px-4 py-2 rounded-lg border ${
                  settings.theme === 'light'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-virgo-teal-700 hover:bg-virgo-navy-600'
                }`}
              >
                <Sun size={18} className="mr-2" />
                Light
              </button>
              <button
                onClick={() => handleChange('theme', 'dark')}
                className={`flex items-center px-4 py-2 rounded-lg border ${
                  settings.theme === 'dark'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-virgo-teal-700 hover:bg-virgo-navy-600'
                }`}
              >
                <Moon size={18} className="mr-2" />
                Dark
              </button>
              <button
                onClick={() => handleChange('theme', 'system')}
                className={`flex items-center px-4 py-2 rounded-lg border ${
                  settings.theme === 'system'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-virgo-teal-700 hover:bg-virgo-navy-600'
                }`}
              >
                <Monitor size={18} className="mr-2" />
                System
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Compact Mode</p>
              <p className="text-sm text-virgo-teal-400">Reduce spacing for more content on screen</p>
            </div>
            <ToggleSwitch
              checked={settings.compactMode}
              onChange={(v) => handleChange('compactMode', v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Collapsed Sidebar by Default</p>
              <p className="text-sm text-virgo-teal-400">Start with the sidebar minimized</p>
            </div>
            <ToggleSwitch
              checked={settings.sidebarCollapsed}
              onChange={(v) => handleChange('sidebarCollapsed', v)}
            />
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
          <Bell className="mr-2 text-virgo-teal-300" size={20} />
          In-App Notifications
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">New Case Assignments</p>
              <p className="text-sm text-virgo-teal-400">Get notified when assigned to a new case</p>
            </div>
            <ToggleSwitch
              checked={settings.notifyNewCase}
              onChange={(v) => handleChange('notifyNewCase', v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">New Messages</p>
              <p className="text-sm text-virgo-teal-400">Get notified for new Sigil messages</p>
            </div>
            <ToggleSwitch
              checked={settings.notifyNewMessage}
              onChange={(v) => handleChange('notifyNewMessage', v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Calendar Reminders</p>
              <p className="text-sm text-virgo-teal-400">Get reminded about upcoming events</p>
            </div>
            <ToggleSwitch
              checked={settings.notifyCalendarReminder}
              onChange={(v) => handleChange('notifyCalendarReminder', v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Task Due Dates</p>
              <p className="text-sm text-virgo-teal-400">Get notified when tasks are due</p>
            </div>
            <ToggleSwitch
              checked={settings.notifyTaskDue}
              onChange={(v) => handleChange('notifyTaskDue', v)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Sound Effects</p>
              <p className="text-sm text-virgo-teal-400">Play sounds for notifications</p>
            </div>
            <ToggleSwitch
              checked={settings.soundEnabled}
              onChange={(v) => handleChange('soundEnabled', v)}
            />
          </div>
        </div>
      </div>

      {/* Default Views */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
          <Layout className="mr-2 text-virgo-teal-300" size={20} />
          Default Views
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Cases View</p>
              <p className="text-sm text-virgo-teal-400">Default view for the Cases page</p>
            </div>
            <select
              value={settings.defaultCasesView}
              onChange={(e) => handleChange('defaultCasesView', e.target.value)}
              className="px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value="list">List View</option>
              <option value="grid">Grid View</option>
              <option value="kanban">Kanban Board</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Contacts View</p>
              <p className="text-sm text-virgo-teal-400">Default view for the Contacts page</p>
            </div>
            <select
              value={settings.defaultContactsView}
              onChange={(e) => handleChange('defaultContactsView', e.target.value)}
              className="px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value="list">List View</option>
              <option value="grid">Grid View</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Items Per Page</p>
              <p className="text-sm text-virgo-teal-400">Number of items to show in lists</p>
            </div>
            <select
              value={settings.casesPerPage}
              onChange={(e) => handleChange('casesPerPage', parseInt(e.target.value))}
              className="px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Work Preferences */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
          <SettingsIcon className="mr-2 text-virgo-teal-300" size={20} />
          Work Preferences
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Auto-Save</p>
              <p className="text-sm text-virgo-teal-400">Automatically save drafts while editing</p>
            </div>
            <ToggleSwitch
              checked={settings.autoSaveEnabled}
              onChange={(v) => handleChange('autoSaveEnabled', v)}
            />
          </div>

          {settings.autoSaveEnabled && (
            <div className="flex items-center justify-between pl-4 border-l-2 border-virgo-teal-800">
              <div>
                <p className="font-medium text-virgo-teal-100">Auto-Save Interval</p>
                <p className="text-sm text-virgo-teal-400">How often to auto-save (seconds)</p>
              </div>
              <select
                value={settings.autoSaveInterval}
                onChange={(e) => handleChange('autoSaveInterval', parseInt(e.target.value))}
                className="px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
              >
                <option value={15}>15 seconds</option>
                <option value={30}>30 seconds</option>
                <option value={60}>1 minute</option>
                <option value={120}>2 minutes</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Astraea Voice Settings */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
          <Sparkles className="mr-2 text-cyan-400" size={20} />
          Astraea Voice Settings
        </h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-virgo-teal-100">Always-On Microphone</p>
              <p className="text-sm text-virgo-teal-400">
                Say "Hey Astraea" anytime to talk to your AI assistant
              </p>
            </div>
            {recognitionSupported ? (
              <ToggleSwitch
                checked={settings.astraeaMicAlwaysOn}
                onChange={(v) => handleAstraeaMicChange(v)}
              />
            ) : (
              <span className="text-sm text-amber-400">
                Voice not supported in this browser
              </span>
            )}
          </div>

          {settings.astraeaMicAlwaysOn && recognitionSupported && (
            <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-cyan-300 font-medium">Voice commands active</p>
                  <p className="text-sm text-cyan-400/70">
                    Try saying "Hey Astraea, what's on my calendar today?"
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="text-sm text-virgo-teal-500 pt-2 border-t border-virgo-navy-600">
            <p>
              <strong>Tip:</strong> You can always click the microphone button in the Astraea chat bubble to talk, regardless of this setting.
            </p>
          </div>
        </div>
      </div>

      {/* Email Configuration Section */}
      <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700 shadow-lg">
        <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
          <Mail className="mr-2 text-virgo-teal-300" size={20} />
          Email Configuration
        </h2>

        {mailMode === 'client' ? (
          <div className="space-y-6">
            <div className="bg-virgo-navy-700 border border-virgo-teal-500/30 rounded-lg p-4 mb-6">
              <p className="text-virgo-navy-100 text-sm">
                <strong className="text-virgo-teal-400">Client Mode:</strong> Connect your external email account (Gmail, Outlook, etc.) to sync with VIRGO.
              </p>
            </div>

            <div className="border border-virgo-navy-600 rounded-lg p-4">
              <h3 className="text-lg font-medium text-virgo-teal-300 mb-4 flex items-center">
                <Server className="mr-2" size={18} />
                Incoming Mail (IMAP)
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-100 mb-2">IMAP Server</label>
                  <input
                    type="text"
                    value={mailSettings.imap_server}
                    onChange={(e) => handleMailSettingChange('imap_server', e.target.value)}
                    placeholder="imap.gmail.com"
                    className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Port</label>
                    <input
                      type="number"
                      value={mailSettings.imap_port}
                      onChange={(e) => handleMailSettingChange('imap_port', parseInt(e.target.value))}
                      className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      checked={mailSettings.imap_use_ssl}
                      onChange={(e) => handleMailSettingChange('imap_use_ssl', e.target.checked)}
                      className="mr-2"
                    />
                    <label className="text-sm text-virgo-teal-100">Use SSL/TLS</label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Username</label>
                  <input
                    type="text"
                    value={mailSettings.imap_username}
                    onChange={(e) => handleMailSettingChange('imap_username', e.target.value)}
                    placeholder="your.email@gmail.com"
                    className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Password</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={mailSettings.imap_password}
                    onChange={(e) => handleMailSettingChange('imap_password', e.target.value)}
                    placeholder="Enter password"
                    className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  />
                </div>
              </div>
            </div>

            <div className="border border-virgo-navy-600 rounded-lg p-4">
              <h3 className="text-lg font-medium text-virgo-teal-300 mb-4 flex items-center">
                <Server className="mr-2" size={18} />
                Outgoing Mail (SMTP)
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-100 mb-2">SMTP Server</label>
                  <input
                    type="text"
                    value={mailSettings.smtp_server}
                    onChange={(e) => handleMailSettingChange('smtp_server', e.target.value)}
                    placeholder="smtp.gmail.com"
                    className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Port</label>
                    <input
                      type="number"
                      value={mailSettings.smtp_port}
                      onChange={(e) => handleMailSettingChange('smtp_port', parseInt(e.target.value))}
                      className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      checked={mailSettings.smtp_use_ssl}
                      onChange={(e) => handleMailSettingChange('smtp_use_ssl', e.target.checked)}
                      className="mr-2"
                    />
                    <label className="text-sm text-virgo-teal-100">Use SSL/TLS</label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Username</label>
                  <input
                    type="text"
                    value={mailSettings.smtp_username}
                    onChange={(e) => handleMailSettingChange('smtp_username', e.target.value)}
                    placeholder="your.email@gmail.com"
                    className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Password</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={mailSettings.smtp_password}
                    onChange={(e) => handleMailSettingChange('smtp_password', e.target.value)}
                    placeholder="Enter password"
                    className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={saveMailSettings}
              disabled={loading}
              className="w-full bg-virgo-teal-500 text-white py-3 rounded-lg hover:bg-virgo-teal-600 disabled:opacity-50 font-medium"
            >
              {loading ? 'Saving...' : 'Save Email Settings'}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-virgo-navy-700 border border-virgo-teal-500/30 rounded-lg p-4 mb-6">
              <p className="text-virgo-navy-100 text-sm">
                <strong className="text-virgo-teal-400">Server Mode:</strong> VIRGO manages your email. Use these settings to configure your email client.
              </p>
            </div>

            <div className="border border-virgo-navy-600 rounded-lg p-4 bg-virgo-navy-700/50">
              <h3 className="text-lg font-medium text-virgo-teal-300 mb-4">Incoming Mail (IMAP)</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-virgo-teal-100">Server:</span>
                  <code className="bg-virgo-navy-900 px-3 py-1 rounded text-virgo-teal-300">Your IMAP server</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-virgo-teal-100">Port:</span>
                  <code className="bg-virgo-navy-900 px-3 py-1 rounded text-virgo-teal-300">993</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-virgo-teal-100">Security:</span>
                  <code className="bg-virgo-navy-900 px-3 py-1 rounded text-virgo-teal-300">SSL/TLS</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-virgo-teal-100">Username:</span>
                  <code className="bg-virgo-navy-900 px-3 py-1 rounded text-virgo-teal-300">{user?.email}</code>
                </div>
              </div>
            </div>

            <div className="border border-virgo-navy-600 rounded-lg p-4 bg-virgo-navy-700/50">
              <h3 className="text-lg font-medium text-virgo-teal-300 mb-4">Outgoing Mail (SMTP)</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-virgo-teal-100">Server:</span>
                  <code className="bg-virgo-navy-900 px-3 py-1 rounded text-virgo-teal-300">Your SMTP server</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-virgo-teal-100">Port:</span>
                  <code className="bg-virgo-navy-900 px-3 py-1 rounded text-virgo-teal-300">587</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-virgo-teal-100">Security:</span>
                  <code className="bg-virgo-navy-900 px-3 py-1 rounded text-virgo-teal-300">STARTTLS</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-virgo-teal-100">Username:</span>
                  <code className="bg-virgo-navy-900 px-3 py-1 rounded text-virgo-teal-300">{user?.email}</code>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Calendar on your devices */}
      <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700 shadow-lg mt-6">
        <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
          <Calendar className="mr-2 text-virgo-teal-300" size={20} />
          Calendar on your devices
        </h2>

        <h3 className="text-lg font-medium text-virgo-teal-300 mb-2">Read-only feed (subscribe)</h3>
        <div className="bg-virgo-navy-700 border border-virgo-teal-500/30 rounded-lg p-4 mb-6">
          <p className="text-virgo-navy-100 text-sm">
            Generate a private link and add it to Google Calendar, Apple Calendar, or Outlook.
            Your VIRGO events (including recurring ones) appear on your devices and refresh
            automatically about once an hour. The link is read-only — nothing on your device
            can change your VIRGO calendar.
          </p>
        </div>

        {feedStatus && feedStatus.status === 'active' && (
          <div className="mb-4 p-3 bg-virgo-navy-700 rounded-lg border border-virgo-navy-600 flex items-center justify-between">
            <span className="text-sm text-virgo-teal-200">
              Feed active since {new Date(feedStatus.created_at).toLocaleDateString()}
              {feedStatus.last_fetched_at && (
                <> · last fetched {new Date(feedStatus.last_fetched_at).toLocaleString()} · {feedStatus.fetch_count} fetches</>
              )}
            </span>
            <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">active</span>
          </div>
        )}

        {feedUrls && (
          <div className="mb-4 p-4 rounded-lg border border-virgo-gold-500/40 bg-virgo-navy-700 space-y-3">
            <p className="text-sm text-virgo-gold-400 font-medium">
              Copy your link now — for security it is shown only once. Generating a new link disables this one.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-virgo-navy-900 px-3 py-2 rounded text-virgo-teal-300 text-xs break-all">{feedUrls.https_url}</code>
              <button type="button" onClick={() => copyFeedUrl('https', feedUrls.https_url)}
                className="px-3 py-2 bg-virgo-navy-600 text-virgo-teal-200 rounded hover:bg-virgo-navy-500 flex items-center">
                <Copy size={14} className="mr-1" />{feedCopied === 'https' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-virgo-navy-900 px-3 py-2 rounded text-virgo-teal-300 text-xs break-all">{feedUrls.webcal_url}</code>
              <button type="button" onClick={() => copyFeedUrl('webcal', feedUrls.webcal_url)}
                className="px-3 py-2 bg-virgo-navy-600 text-virgo-teal-200 rounded hover:bg-virgo-navy-500 flex items-center">
                <Copy size={14} className="mr-1" />{feedCopied === 'webcal' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        )}

        <div className="border border-virgo-navy-600 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-virgo-teal-300 mb-2">How to subscribe</h3>
          <ul className="text-sm text-virgo-teal-400 space-y-1 list-disc list-inside">
            <li><strong className="text-virgo-teal-200">Google Calendar:</strong> Other calendars → “+” → From URL → paste the https link</li>
            <li><strong className="text-virgo-teal-200">Apple Calendar (Mac):</strong> File → New Calendar Subscription → paste the link</li>
            <li><strong className="text-virgo-teal-200">iPhone/iPad:</strong> Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar</li>
            <li><strong className="text-virgo-teal-200">Outlook:</strong> Add calendar → Subscribe from web → paste the https link</li>
          </ul>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={mintFeedToken}
            disabled={feedLoading}
            className="flex-1 bg-virgo-teal-500 text-white py-3 rounded-lg hover:bg-virgo-teal-600 disabled:opacity-50 font-medium flex items-center justify-center"
          >
            <RefreshCw className={`mr-2 ${feedLoading ? 'animate-spin' : ''}`} size={18} />
            {feedStatus && feedStatus.status === 'active' ? 'Generate New Link (replaces current)' : 'Generate Feed Link'}
          </button>
          {feedStatus && feedStatus.status === 'active' && (
            <button
              type="button"
              onClick={revokeFeedToken}
              disabled={feedLoading}
              className="flex-1 bg-virgo-navy-600 text-red-300 py-3 rounded-lg hover:bg-virgo-navy-500 disabled:opacity-50 font-medium"
            >
              Revoke Feed Link
            </button>
          )}
        </div>

        {/* Two-way sync (CalDAV device passwords) */}
        <div className="mt-8 pt-6 border-t border-virgo-navy-600">
          <h3 className="text-lg font-medium text-virgo-teal-300 mb-2">Two-way sync (CalDAV)</h3>
          <div className="bg-virgo-navy-700 border border-virgo-teal-500/30 rounded-lg p-4 mb-4">
            <p className="text-virgo-navy-100 text-sm">
              For full two-way sync — where events you add on your phone also appear in VIRGO —
              add a CalDAV account on your device using your email and a <strong className="text-virgo-teal-300">device
              password</strong> below. Device passwords are separate from your login password, so you can
              revoke a lost device without changing how you sign in.
            </p>
          </div>

          {newDevicePassword && (
            <div className="mb-4 p-4 rounded-lg border border-virgo-gold-500/40 bg-virgo-navy-700 space-y-2">
              <p className="text-sm text-virgo-gold-400 font-medium">
                Copy this device password now — for security it is shown only once.
              </p>
              <p className="text-xs text-virgo-teal-400">Device: {newDevicePassword.label}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-virgo-navy-900 px-3 py-2 rounded text-virgo-teal-300 text-sm break-all">{newDevicePassword.password}</code>
                <button type="button" onClick={() => copyDevicePassword(newDevicePassword.password)}
                  className="px-3 py-2 bg-virgo-navy-600 text-virgo-teal-200 rounded hover:bg-virgo-navy-500 flex items-center">
                  <Copy size={14} className="mr-1" />{devicePwCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <button type="button" onClick={() => setNewDevicePassword(null)}
                className="text-xs text-virgo-teal-500 hover:text-virgo-teal-300 mt-1">I've saved it — dismiss</button>
            </div>
          )}

          {devicePasswords.length > 0 && (
            <div className="mb-4 space-y-2">
              {devicePasswords.map((dp) => (
                <div key={dp.id} className="flex items-center justify-between p-3 bg-virgo-navy-700 rounded-lg border border-virgo-navy-600">
                  <div>
                    <p className="text-sm text-virgo-teal-100 font-medium">{dp.label}</p>
                    <p className="text-xs text-virgo-teal-500">
                      Added {new Date(dp.created_at).toLocaleDateString()}
                      {dp.last_used_at ? ` · last used ${new Date(dp.last_used_at).toLocaleString()}` : ' · never used'}
                    </p>
                  </div>
                  <button type="button" onClick={() => revokeDevicePassword(dp.id)} disabled={deviceLoading}
                    className="text-xs px-3 py-1 bg-virgo-navy-600 text-red-300 rounded hover:bg-virgo-navy-500 disabled:opacity-50">
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            <input
              type="text"
              value={newDeviceLabel}
              onChange={(e) => setNewDeviceLabel(e.target.value)}
              placeholder='Device name (e.g. "iPhone")'
              maxLength={100}
              className="flex-1 px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
            />
            <button type="button" onClick={mintDevicePassword} disabled={deviceLoading}
              className="bg-virgo-teal-500 text-white px-5 py-2 rounded-lg hover:bg-virgo-teal-600 disabled:opacity-50 font-medium">
              {deviceLoading ? 'Working…' : 'Add device password'}
            </button>
          </div>

          <div className="border border-virgo-navy-600 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-medium text-virgo-teal-300 mb-2">Add a CalDAV account</h4>
            <ul className="text-sm text-virgo-teal-400 space-y-1 list-disc list-inside">
              <li><strong className="text-virgo-teal-200">iPhone/iPad:</strong> Settings → Calendar → Accounts → Add Account → Other → Add CalDAV Account. Server: <code className="text-virgo-teal-300">app.virgolaw.app</code>, User Name: your email, Password: the device password above.</li>
              <li><strong className="text-virgo-teal-200">Apple Calendar (Mac):</strong> Calendar → Settings → Accounts → “+” → Other CalDAV Account → Manual. Server: <code className="text-virgo-teal-300">app.virgolaw.app</code>.</li>
              <li><strong className="text-virgo-teal-200">Thunderbird / DAVx5:</strong> Base URL <code className="text-virgo-teal-300">https://app.virgolaw.app/caldav/</code>, email + device password.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Timezone Settings Section */}
      {user?.role === 'admin' && (
        <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700 shadow-lg mt-6">
          <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
            <Globe className="mr-2 text-virgo-teal-300" size={20} />
            Firm Timezone Settings
          </h2>

          <div className="bg-virgo-navy-700 border border-virgo-teal-500/30 rounded-lg p-4 mb-6">
            <p className="text-virgo-navy-100 text-sm">
              <strong className="text-virgo-teal-400">Timezone Override:</strong> When enabled, all dates and times in the system will display in the selected timezone, regardless of each user's browser settings. This ensures consistency across VPNs and travel.
            </p>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">
                  <Clock className="inline mr-1" size={14} />
                  Firm Timezone
                </label>
                <select
                  value={timezoneSettings.timezone}
                  onChange={(e) => setTimezoneSettings(prev => ({ ...prev, timezone: e.target.value }))}
                  className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                >
                  {timezoneOptions.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.abbr})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">
                  Override Browser Timezone
                </label>
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={timezoneSettings.timezone_override_enabled}
                    onChange={(e) => setTimezoneSettings(prev => ({ ...prev, timezone_override_enabled: e.target.checked }))}
                    className="h-5 w-5 text-virgo-teal-500 focus:ring-virgo-teal-400 border-virgo-navy-500 rounded bg-virgo-navy-900"
                  />
                  <span className="ml-3 text-virgo-navy-100">
                    {timezoneSettings.timezone_override_enabled
                      ? 'Using firm timezone for all users'
                      : 'Using each user\'s browser timezone'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={saveTimezoneSettings}
                disabled={timezoneLoading}
                className="px-4 py-2 bg-virgo-teal-500 text-white rounded-lg hover:bg-virgo-teal-600 disabled:opacity-50 flex items-center"
              >
                {timezoneLoading ? 'Saving...' : 'Save Timezone Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firm Billing Settings Section */}
      <div className="bg-virgo-navy-800 rounded-lg p-6 border border-virgo-navy-700 shadow-lg mt-6">
        <h2 className="text-xl font-semibold text-virgo-teal-100 mb-4 flex items-center">
          <DollarSign className="mr-2 text-virgo-teal-300" size={20} />
          Firm Billing Rates
        </h2>

        <div className="bg-virgo-navy-700 border border-virgo-teal-500/30 rounded-lg p-4 mb-6">
          <p className="text-virgo-navy-100 text-sm">
            <strong className="text-virgo-teal-400">Default Rates:</strong> These rates apply to all cases unless overridden in case-specific settings.
          </p>
        </div>

        <div className="space-y-6">
          {/* Hourly Rates */}
          <div className="border border-virgo-navy-600 rounded-lg p-4">
            <h3 className="text-lg font-medium text-virgo-teal-300 mb-4">Default Hourly Rates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Partner Rate</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={billingSettings.partner_rate || ''}
                    onChange={(e) => handleBillingSettingChange('partner_rate', parseFloat(e.target.value) || 0)}
                    className="w-full pl-8 pr-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    placeholder="450.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Attorney Rate</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={billingSettings.attorney_rate || ''}
                    onChange={(e) => handleBillingSettingChange('attorney_rate', parseFloat(e.target.value) || 0)}
                    className="w-full pl-8 pr-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    placeholder="350.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Associate Rate</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={billingSettings.associate_rate || ''}
                    onChange={(e) => handleBillingSettingChange('associate_rate', parseFloat(e.target.value) || 0)}
                    className="w-full pl-8 pr-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    placeholder="275.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Paralegal Rate</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={billingSettings.paralegal_rate || ''}
                    onChange={(e) => handleBillingSettingChange('paralegal_rate', parseFloat(e.target.value) || 0)}
                    className="w-full pl-8 pr-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    placeholder="150.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Admin Rate</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={billingSettings.admin_rate || ''}
                    onChange={(e) => handleBillingSettingChange('admin_rate', parseFloat(e.target.value) || 0)}
                    className="w-full pl-8 pr-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    placeholder="75.00"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Billing Methods & Increments */}
          <div className="border border-virgo-navy-600 rounded-lg p-4">
            <h3 className="text-lg font-medium text-virgo-teal-300 mb-4">Billing Defaults</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Default Billing Method</label>
                <select
                  value={billingSettings.default_billing_method || 'hourly'}
                  onChange={(e) => handleBillingSettingChange('default_billing_method', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                >
                  <option value="hourly">Hourly</option>
                  <option value="flat_fee">Flat Fee</option>
                  <option value="contingency">Contingency</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Default Contingency Rate</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={billingSettings.default_contingency_rate || ''}
                    onChange={(e) => handleBillingSettingChange('default_contingency_rate', parseFloat(e.target.value) || 0)}
                    className="w-full pr-8 pl-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    placeholder="33.33"
                  />
                  <span className="absolute right-3 top-2 text-virgo-teal-400">%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Minimum Increment (hours)</label>
                <select
                  value={billingSettings.minimum_increment || 0.1}
                  onChange={(e) => handleBillingSettingChange('minimum_increment', parseFloat(e.target.value))}
                  className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                >
                  <option value={0.1}>0.1 (6 minutes)</option>
                  <option value={0.25}>0.25 (15 minutes)</option>
                  <option value={0.5}>0.5 (30 minutes)</option>
                  <option value={1.0}>1.0 (1 hour)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Rounding Method</label>
                <select
                  value={billingSettings.rounding_method || 'up'}
                  onChange={(e) => handleBillingSettingChange('rounding_method', e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                >
                  <option value="up">Round Up</option>
                  <option value="down">Round Down</option>
                  <option value="nearest">Round to Nearest</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Payment Terms (days)</label>
                <input
                  type="number"
                  value={billingSettings.payment_terms_days || 30}
                  onChange={(e) => handleBillingSettingChange('payment_terms_days', parseInt(e.target.value) || 30)}
                  className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                  placeholder="30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-100 mb-2">Default Tax Rate</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={billingSettings.default_tax_rate || 0}
                    onChange={(e) => handleBillingSettingChange('default_tax_rate', parseFloat(e.target.value) || 0)}
                    className="w-full pr-8 pl-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-2 text-virgo-teal-400">%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={saveBillingSettings}
            disabled={billingLoading}
            className="w-full bg-virgo-teal-500 text-white py-3 rounded-lg hover:bg-virgo-teal-600 disabled:opacity-50 font-medium"
          >
            {billingLoading ? 'Saving...' : 'Save Billing Settings'}
          </button>
        </div>
      </div>

      {/* AstraeaMesh Network */}
      <div className="mt-6">
        <MeshSettings />
      </div>

      {/* Trust Accounts Section - For reviewing Clio imports */}
      <div className="mt-6">
        <StaffTrustAccounts />
      </div>

      {/* IT Support Access Panel - Admin only */}
      {user?.role === 'admin' && (
        <SupportAccessPanel />
      )}
      </div>
    </div>
  );
};

export default Settings;

